import React, {useState, useEffect} from 'react';
import axios from 'axios';
import AdminMenu from "../AdminMenu";
import AdminHeader from "../AdminHeader";
import Button from "../../../components/elements/Button";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAdd, faSave, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import InputField from "../../../components/elements/InputField";
import {useNavigate, useParams} from 'react-router-dom';
import ErrorMessage from "../../../components/layout/ErrorMessage";
import {ADMIN_API_BASE} from "../../../utils/apiConfig";
import TextEditor from "../../../components/elements/TextEditor";
import CustomSelect from "../../../components/elements/CustomSelect";
import CheatsheetEditor from "../courses/content/cheatsheet/CheatsheetEditor";
import JsonAdvancedEditor from "../courses/content/shared/JsonAdvancedEditor";

const AdminCheatsheetPage = () => {
    const [cheatsheet, setCheatsheet] = useState({
    });

    const [courseId, setCourseId] = useState(useParams().course_id);
    const [cheatsheetTranslations, setCheatsheetTranslations] = useState([]);
    const [cheatsheetId, setCheatsheetId] = useState(useParams().id);
    const [insertLanguage, setInsertLanguage] = useState('');
    const [languagesOptions, setLanguageOptions] = useState([]);
    const [message, setMessage] = useState("");
    const navigate = useNavigate();

    const getCsrfToken = async () => {
        const response = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            method: "GET",
            credentials: "include",
        });

        const data = await response.json();
        return data.csrfToken;
    };

    useEffect(() => {

        const fetchCheatsheet = async (idToFetch) => {
            if (!idToFetch) {
                return;
            }
            try {
                const token = await getCsrfToken();
                const response = await axios.get(`${ADMIN_API_BASE}/${courseId}/cheatsheet/${idToFetch}`, {
                    withCredentials: true,
                    headers: {
                        'csrf-token': token,
                    },
                });
                if (response.data.success) {
                    setCheatsheet(response.data.cheatsheet);

                    setCheatsheetTranslations(
                        (response.data.cheatsheet.translations || []).map(t => ({
                            lang: t.language_id,
                            title: t.title,
                            subtitle: t.subtitle,
                            content: t.content
                        }))
                    );
                    setCheatsheetId(response.data.cheatsheet.id);
                } else {
                    console.error("Draft not found or invalid response");
                    setMessage({type: 'error', text: 'Draft not found.'});
                }
            } catch (error) {
                console.error("Error fetching draft:", error);
                setMessage({type: 'error', text: 'Error fetching draft: ' + error.message});
            }
        };

        if (cheatsheetId !== 'new') {
            fetchCheatsheet(cheatsheetId);
        }

    }, [cheatsheetId]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;

        setCheatsheet((prevCheatsheet) => ({...prevCheatsheet, [name]: value}));
    };

    const updateCheatsheet = async () => {
        try {
            const token = await getCsrfToken();
            let response;
            if (cheatsheetId !== 'new') {
                response = await axios.put(`${ADMIN_API_BASE}/${courseId}/cheatsheet/${cheatsheetId}`, cheatsheet, {
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                });
                setCheatsheetId(cheatsheet.id)
                navigate(`/admin/course/${courseId}/cheatsheet/${cheatsheet.id}`);
            } else {
                response = await axios.post(`${ADMIN_API_BASE}/${courseId}/cheatsheet/`, cheatsheet, {
                    headers: {
                        'csrf-token': token,
                    },
                    withCredentials: true
                });
                setCheatsheetId(cheatsheet.id)
                navigate(`/admin/course/${courseId}/cheatsheet/${cheatsheet.id}`);
            }

            setMessage({type: "success", text: "cheatsheet updated successfully!"});
        } catch (error) {
            setMessage({type: "error", text: "err: " + (error.response?.data?.error || error.message)});
        }
    };

    const deleteCheatsheet = async () => {
        const token = await getCsrfToken();
        await axios.delete(`${ADMIN_API_BASE}/${courseId}/cheatsheet/${cheatsheetId}`, {
            headers: {
                'csrf-token': token,
            },
            withCredentials: true
        });
        useNavigate();
    }

    const fetchUpdatedCheatsheet = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/${courseId}/cheatsheet/${cheatsheetId}`, {
                withCredentials: true,
                headers: {
                    'csrf-token': token,
                },
            });
            if (res.data && res.data.success) {
                setCheatsheet(res.data.cheatsheet);
            }
        } catch (error) {
        }
    };


    useEffect(() => {
        fetchLanguages();
    }, [])

    const fetchLanguages = async () => {
        try {
            const token = await getCsrfToken();
            const res = await axios.get(`${ADMIN_API_BASE}/language`,
                {
                    withCredentials: true,
                    headers: {
                        'csrf-token': token,
                    },
                }
            );

            if (res.data.success) {
                const options = res.data.languages.languages.map(l => l.id);
                setLanguageOptions(options);
            } else {
                setMessage(res.data.error);
            }
        } catch (e) {
            setMessage(e);
        }
    }

    const isTranslationExists = (lang) => {
        return cheatsheetTranslations.some(t => t.lang === lang);
    }

    const handleAddTranslation = () => {

        if (!insertLanguage || isTranslationExists(insertLanguage)) {
            return;
        }

        setCheatsheetTranslations(prev => [
            ...prev,
            {
                lang: insertLanguage,
                title: "",
                subtitle: "",
                content: {
                    version: 1,
                    blocks: []
                }
            }
        ]);

        setInsertLanguage("");

    };
    const handleDeleteTranslation = (langToRemove) => {
        setCheatsheetTranslations(prev =>
            prev.filter(t => t.lang !== langToRemove)
        );
    };

    const handleTranslationChange = (index, field, value) => {
        setCheatsheetTranslations(prev => {
            const updated = [...prev];
            updated[index][field] = value;
            return updated;
        });
    };

    const handleInsertTranslation = (translation) => {

        setCheatsheet(prev => {

            const translations = Array.isArray(prev.translations)
                ? [...prev.translations]
                : [];

            const index = translations.findIndex(
                t => t.language_id === translation.lang
            );

            const payload = {
                language_id: translation.lang,
                title: translation.title,
                subtitle: translation.subtitle,
                content: translation.content
            };

            if (index >= 0) {
                translations[index] = payload;
            } else {
                translations.push(payload);
            }

            return {
                ...prev,
                translations
            };

        });

        setMessage({
            type: "success",
            text: `Translation "${translation.lang}" added to cheatsheet.`
        });

    };

    const validateCheatsheetJson = (json) => {
        if (json.version !== 1) {
            return "version باید 1 باشد.";
        }

        if (!Array.isArray(json.blocks)) {
            return "blocks باید یک آرایه باشد.";
        }

        const allowedTypes = ["text", "code", "link"];

        for (let i = 0; i < json.blocks.length; i++) {
            const block = json.blocks[i];

            if (!block || typeof block !== "object") {
                return `Block ${i + 1} باید یک object باشد.`;
            }

            if (!block.id) {
                return `Block ${i + 1} باید id داشته باشد.`;
            }

            if (!allowedTypes.includes(block.type)) {
                return `Block ${i + 1}: نوع "${block.type}" پشتیبانی نمی‌شود.`;
            }

            if (!block.config || typeof block.config !== "object") {
                return `Block ${i + 1} باید config داشته باشد.`;
            }

            if (block.type === "text") {
                if (typeof block.config.text !== "string") {
                    return `Block ${i + 1}: text باید string باشد.`;
                }
            }

            if (block.type === "code") {
                if (typeof block.config.code !== "string") {
                    return `Block ${i + 1}: code باید string باشد.`;
                }

                if (typeof block.config.language !== "string") {
                    return `Block ${i + 1}: language باید string باشد.`;
                }
            }

            if (block.type === "link") {
                if (typeof block.config.text !== "string") {
                    return `Block ${i + 1}: link text باید string باشد.`;
                }

                if (typeof block.config.url !== "string") {
                    return `Block ${i + 1}: link url باید string باشد.`;
                }
            }
        }

        return null;
    };

    return (
        <div className="flex flex-1">
            <div className="flex flex-1">
                <div className='w-1/5'>
                    <AdminMenu path='./'/>
                </div>
                <div className='flex-grow relative w-4/5'>
                    <AdminHeader title={` تقلب‌نامه ${cheatsheetId}`}/>
                    {message.text && <ErrorMessage type={message.type} message={message.text}/>}
                    <form encType='multipart/form-data' className='px-8 py-5' onSubmit={(e) => e.preventDefault()}>
                        <div className='flex gap-8 justify-end'>
                            <Button className='p-1 bg-light-primary text-white'
                                    onClick={updateCheatsheet}>
                                <FontAwesomeIcon icon={faSave} className='h-4'/>
                                <span>{cheatsheetId !== 'new' ? 'ذخیره' : 'انتشار'}</span>
                            </Button>

                            {cheatsheetId !== 'new' && (
                                <Button className='p-1 bg-light-primary text-white'
                                        onClick={deleteCheatsheet}>
                                    <FontAwesomeIcon icon={faTrashCan} className='h-4'/>
                                    <span>حذف</span>
                                </Button>
                            )}
                        </div>
                        <div className='w-1/2 mb-8'>
                            <InputField name='courseId' title='course_id' disabled={true} type='text' value={courseId || ''} />
                        </div>
                        <div className='w-1/2 mb-8'>
                            <InputField name='id' title='id' type='text' value={cheatsheet.id || ''}
                                        onChange={handleInputChange}/>
                        </div>
                        <div className='w-full flex gap-5 border-t-2 border-dashed border-gray-400 pt-5'>
                            <CustomSelect
                                value={insertLanguage}
                                options={languagesOptions}
                                title="زبان"
                                onChange={(e) =>
                                    setInsertLanguage(e.target.value)
                                }
                            />
                            <Button>
                                <FontAwesomeIcon icon={faAdd} size={'lg'} onClick={handleAddTranslation} />
                            </Button>
                        </div>
                        <div className='w-full flex flex-col gap-8 justify-center'>
                            {
                                cheatsheetTranslations.map((item, index) => (
                                    <div key={index} className='border p-5 mt-10 rounded-lg flex flex-col gap-5'>
                                        <div className='font-bold flex justify-between items-center'>
                                            <span>Language: {item.lang}</span>
                                            <div className='flex items-center gap-3'>
                                                <Button onClick={() => handleDeleteTranslation(item.lang)}>
                                                    <FontAwesomeIcon icon={faTrashCan} size={'lg'} />
                                                </Button>
                                                <Button onClick={() => handleInsertTranslation(item)}>
                                                    <FontAwesomeIcon icon={faSave} size={'lg'} />
                                                </Button>
                                            </div>
                                        </div>

                                        <div className='w-1/2'>
                                            <InputField
                                                name={`title-${item.lang}`}
                                                title='تیتر'
                                                type='text'
                                                value={item.title}
                                                onChange={(e) =>
                                                    handleTranslationChange(index, 'title', e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className='w-1/2'>
                                            <InputField
                                                name={`subtitle-${item.lang}`}
                                                title='زیرتیتر'
                                                type='text'
                                                value={item.subtitle}
                                                onChange={(e) =>
                                                    handleTranslationChange(index, 'subtitle', e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className='w-full'>
                                            <CheatsheetEditor
                                                value={item.content}
                                                onChange={(value) =>
                                                    handleTranslationChange(index, "content", value)
                                                }
                                            />
                                        </div>

                                    </div>
                                ))
                            }
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminCheatsheetPage;